/**
 * How often somebody's notices are mailed, and so when the next mail may go.
 *
 * Every notice is in the app the moment it is made; this only decides the
 * mail. A window is a ceiling, not a delay: the first notice after a quiet
 * spell goes at once, and whatever follows inside the window waits and goes
 * as one digest when the window ends. So a handoff after a quiet afternoon is
 * as fast as it ever was, and somebody finishing ten cards in a row sends
 * their colleagues one mail each rather than ten. See decisions.md, "Mail
 * goes at once, then at most once a window".
 */

export type MailEvery = "now" | "15m" | "1h" | "never" | "off";

export const MAIL_EVERY: Record<MailEvery, { label: string; minutes: number | null }> = {
  now: { label: "Every change, straight away", minutes: 0 },
  "15m": { label: "At most every 15 minutes", minutes: 15 },
  "1h": { label: "At most once an hour", minutes: 60 },
  never: { label: "Only urgent ones", minutes: null },
  off: { label: "Never email me", minutes: null },
};

export const MAIL_EVERY_OPTIONS = Object.keys(MAIL_EVERY) as MailEvery[];

/**
 * Which notices are mailed at all, apart from how often. Everything is always
 * in the app; this is only the inbox. "Only what needs me" leaves out the
 * notices that tell somebody something happened (`noticeTier()`), which was
 * most of what read as noise.
 */
export type MailWhat = "needs-me" | "all";

export const MAIL_WHAT: Record<MailWhat, { label: string }> = {
  "needs-me": { label: "Only what needs me" },
  all: { label: "Everything, including what happened" },
};

export const MAIL_WHAT_OPTIONS = Object.keys(MAIL_WHAT) as MailWhat[];

export const DEFAULT_MAIL_WHAT: MailWhat = "needs-me";

export function checkMailWhat(value: unknown): { ok: true; what: MailWhat } | { ok: false; reason: string } {
  return MAIL_WHAT_OPTIONS.includes(value as MailWhat)
    ? { ok: true, what: value as MailWhat }
    : { ok: false, reason: "Choose which notices are mailed to you." };
}

/**
 * How long after their last request somebody still counts as in the app. The
 * open app asks for its unread count every minute while it is on screen, so a
 * little more than that is "looking at it now"; a tab left in the background
 * stops asking and soon stops counting.
 */
export const ACTIVE_WITHIN_MS = 90_000;

/** Fast after a quiet spell, quiet during a burst. */
export const DEFAULT_MAIL_EVERY: MailEvery = "15m";

export function checkMailEvery(
  value: unknown,
): { ok: true; every: MailEvery } | { ok: false; reason: string } {
  return MAIL_EVERY_OPTIONS.includes(value as MailEvery)
    ? { ok: true, every: value as MailEvery }
    : { ok: false, reason: "Choose how often notices are mailed to you." };
}

/**
 * When the mail for a new notice may go: `now` itself, a later moment to hold
 * it until, or null for no mail (it is still in the app). `atOnce` is for what
 * may not wait for a window: a time-critical card (an access revocation that
 * waits for a digest is the failure Stage 2's gate is about), and an urgent
 * notice for somebody who chose "only urgent ones". Merely overdue is urgent
 * but keeps to the window, or it would override every choice made here.
 *
 * `lastSentAt` is when this person was last mailed a notice, or null if never;
 * milliseconds, like `now`.
 */
export function mailDueAt(
  every: MailEvery,
  lastSentAt: number | null,
  atOnce: boolean,
  now: number,
): number | null {
  // Never means never: the notice is in the app, and that is the only place it is.
  if (every === "off") return null;
  if (atOnce) return now;
  const { minutes } = MAIL_EVERY[every] ?? MAIL_EVERY[DEFAULT_MAIL_EVERY];
  if (minutes === null) return null;
  if (lastSentAt === null) return now;
  return Math.max(now, lastSentAt + minutes * 60_000);
}
